/**
 * @file     attribution.ts
 * @purpose  One definition of "where did this person come from". Resolves a
 *           channel from a landing URL and referrer, with the ad click id read
 *           first and the referrer only as a fallback: Meta strips the referrer
 *           in its in-app browsers, so a referrer-first model reports paid
 *           social as zero while the fbclid sits in the URL. Pure and
 *           runtime-agnostic so middleware, the lead route, and the CRM
 *           formatter all agree.
 */

export const CLICK_ID_PARAMS: ReadonlyArray<{
  param: string;
  network: string;
  channel: Channel;
}> = [
  { param: 'fbclid', network: 'Meta (Facebook / Instagram)', channel: 'paid_social' },
  { param: 'gclid', network: 'Google Ads', channel: 'paid_search' },
  { param: 'gbraid', network: 'Google Ads', channel: 'paid_search' },
  { param: 'wbraid', network: 'Google Ads', channel: 'paid_search' },
  { param: 'msclkid', network: 'Microsoft Ads', channel: 'paid_search' },
  { param: 'ttclid', network: 'TikTok', channel: 'paid_social' },
  { param: 'li_fat_id', network: 'LinkedIn', channel: 'paid_social' },
];

export const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;

export type Channel =
  | 'paid_search'
  | 'paid_social'
  | 'organic_search'
  | 'organic_social'
  | 'referral'
  | 'email'
  | 'internal'
  | 'direct';

export interface ClickId {
  param: string;
  value: string;
  network: string;
  channel: Channel;
}

export interface ResolvedChannel {
  channel: Channel;
  /** Human label for the CRM note and admin views. */
  source: string;
  clickId?: ClickId;
  campaign?: Record<string, string>;
  /** Referring host, when the visit came from another site. */
  referrerHost?: string;
}

const SEARCH_HOSTS = /(^|\.)(google|bing|duckduckgo|yahoo|ecosia|brave)\./i;
const SOCIAL_HOSTS = /(^|\.)(facebook|instagram|l\.facebook|lm\.facebook|t\.co|x|twitter|linkedin|reddit|tiktok|pinterest)\./i;
const MAIL_HOSTS = /(^|\.)(mail\.google|outlook|mail\.yahoo)\.|^com\.google\.android\.gm$/i;

export function isOwnHost(host: string): boolean {
  return /(^|\.)relta\.ca$/i.test(host) || host === 'localhost';
}

/** First click id present on a landing URL's query string, if any. */
export function readClickId(params: URLSearchParams): ClickId | null {
  for (const entry of CLICK_ID_PARAMS) {
    const value = params.get(entry.param);
    if (value) {
      return { param: entry.param, value, network: entry.network, channel: entry.channel };
    }
  }
  return null;
}

/** The utm_* pairs present on a landing URL, or null when there are none. */
export function readCampaign(params: URLSearchParams): Record<string, string> | null {
  const out: Record<string, string> = {};
  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value) out[key] = value;
  }
  return Object.keys(out).length > 0 ? out : null;
}

export const SHARE_REF_PARAM = 'ref';
const SHARE_REF_RE = /^[a-z0-9_-]{1,32}$/i;

/** A share tag such as `app_share`. Free text is refused so it can't carry PII into analytics. */
export function readShareRef(params: Pick<URLSearchParams, 'get'>): string | null {
  const value = params.get(SHARE_REF_PARAM);
  return value && SHARE_REF_RE.test(value) ? value.toLowerCase() : null;
}

/** Keep a share tag across a canonicalising redirect so attribution survives it. */
export function carryShareRef(from: Pick<URLSearchParams, 'get'>, to: URL): void {
  const ref = readShareRef(from);
  if (ref) to.searchParams.set(SHARE_REF_PARAM, ref);
}

/**
 * Resolve the acquisition channel. Precedence is deliberate:
 * click id → utm_medium/source → referrer host → direct.
 */
export function resolveChannel(entryUrl?: string | null, referrer?: string | null): ResolvedChannel {
  let params: URLSearchParams | null = null;
  try {
    if (entryUrl) params = new URL(entryUrl).searchParams;
  } catch {
    params = null;
  }

  const clickId = params ? readClickId(params) : null;
  const campaign = params ? readCampaign(params) : null;

  let referrerHost: string | undefined;
  try {
    if (referrer) {
      const host = new URL(referrer).hostname;
      if (!isOwnHost(host)) referrerHost = host;
    }
  } catch {
    referrerHost = undefined;
  }

  if (clickId) {
    return {
      channel: clickId.channel,
      source: clickId.network,
      clickId,
      ...(campaign ? { campaign } : {}),
      ...(referrerHost ? { referrerHost } : {}),
    };
  }

  if (campaign) {
    const medium = campaign.utm_medium?.toLowerCase() ?? '';
    const source = campaign.utm_source ?? 'campaign';
    const channel: Channel = /cpc|ppc|paid/.test(medium)
      ? /social|facebook|instagram|meta|tiktok/.test(`${medium}${source}`)
        ? 'paid_social'
        : 'paid_search'
      : /email|newsletter/.test(medium)
        ? 'email'
        : /social/.test(medium)
          ? 'organic_social'
          : 'referral';
    return { channel, source, campaign, ...(referrerHost ? { referrerHost } : {}) };
  }

  if (referrerHost) {
    if (MAIL_HOSTS.test(referrerHost)) return { channel: 'email', source: referrerHost, referrerHost };
    if (SEARCH_HOSTS.test(referrerHost)) return { channel: 'organic_search', source: referrerHost, referrerHost };
    if (SOCIAL_HOSTS.test(referrerHost)) return { channel: 'organic_social', source: referrerHost, referrerHost };
    return { channel: 'referral', source: referrerHost, referrerHost };
  }

  return { channel: 'direct', source: 'Direct or untagged' };
}

const CHANNEL_LABELS: Record<Channel, string> = {
  paid_search: 'Paid search',
  paid_social: 'Paid social',
  organic_search: 'Organic search',
  organic_social: 'Social',
  referral: 'Referral',
  email: 'Email',
  internal: 'Internal',
  direct: 'Direct',
};

export function channelLabel(channel: Channel): string {
  return CHANNEL_LABELS[channel];
}
